import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { TransitionService, HeroElementData } from '@services/transition.service';
import { overlayFadeAnimation } from '@common/animations/regiment-transitions';

/**
 * Hero Transition Overlay Component
 * 
 * Renders the "flying" hero icon during shared element transitions.
 * This overlay sits above all other content and animates the icon
 * from its source position to its destination.
 */
@Component({
  selector: 'app-hero-transition-overlay',
  template: `
    <div 
      class="hero-overlay" 
      *ngIf="isActive"
      [@overlayFade]>
      
      <!-- Background dim -->
      <div class="overlay-backdrop" [class.visible]="showBackdrop"></div>
      
      <!-- Flying hero icon -->
      <div 
        class="hero-icon-container"
        [style.left.px]="iconPosition.left"
        [style.top.px]="iconPosition.top"
        [style.width.px]="iconPosition.width"
        [style.height.px]="iconPosition.height"
        [style.transform]="iconTransform"
        [class.animating]="isAnimating">
        <img 
          [src]="heroData?.iconSrc" 
          [alt]="heroData?.displayName"
          class="hero-icon">
      </div>
    </div>
  `,
  styles: [`
    .hero-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      pointer-events: none;
    }

    .overlay-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0);
      transition: background 300ms ease-out;
    }

    .overlay-backdrop.visible {
      background: rgba(0, 0, 0, 0.1);
    }

    .hero-icon-container {
      position: fixed;
      border-radius: 12px;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      transition: none;
      will-change: transform, left, top, width, height;
    }

    .hero-icon-container.animating {
      transition: transform 450ms cubic-bezier(0.16, 1, 0.3, 1),
                  left 450ms cubic-bezier(0.16, 1, 0.3, 1),
                  top 450ms cubic-bezier(0.16, 1, 0.3, 1),
                  width 450ms cubic-bezier(0.16, 1, 0.3, 1),
                  height 450ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-icon {
      width: 80%;
      height: 80%;
      object-fit: contain;
    }
  `],
  animations: [overlayFadeAnimation]
})
export class HeroTransitionOverlayComponent implements OnInit, OnDestroy {

  /** Whether the overlay is active */
  isActive = false;

  /** Whether the icon is currently animating */
  isAnimating = false;

  /** Whether to show the backdrop dim */
  showBackdrop = false;

  /** Current hero element data */
  heroData: HeroElementData | null = null;

  /** Current icon position */
  iconPosition = {
    left: 0,
    top: 0,
    width: 48,
    height: 48
  };

  /** Current icon transform */
  iconTransform = 'scale(1)';

  private subscriptions: Subscription[] = [];

  constructor(
    private transitionService: TransitionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribe to hero animation start
    const sub = this.transitionService.heroAnimationStart$.subscribe(heroData => {
      this.startHeroAnimation(heroData);
    });
    this.subscriptions.push(sub);

    // Subscribe to transition complete
    const completeSub = this.transitionService.transitionComplete$.subscribe(() => {
      this.completeAnimation();
    });
    this.subscriptions.push(completeSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Start the hero animation
   */
  private startHeroAnimation(heroData: HeroElementData): void {
    this.heroData = heroData;
    this.isActive = true;
    this.showBackdrop = true;

    // Set initial position from the source element
    this.iconPosition = {
      left: heroData.startRect.left,
      top: heroData.startRect.top,
      width: heroData.startRect.width,
      height: heroData.startRect.height
    };

    this.iconTransform = 'scale(1)';
    this.cdr.detectChanges();

    // Start animation after a frame
    requestAnimationFrame(() => {
      this.isAnimating = true;
      
      // Animate to center-top of screen (destination approximation)
      // The actual destination will be refined when the target component loads
      const targetLeft = (window.innerWidth / 2) - 40;
      const targetTop = 120;
      const targetSize = 80;

      this.iconPosition = {
        left: targetLeft,
        top: targetTop,
        width: targetSize,
        height: targetSize
      };

      this.iconTransform = 'scale(1.1)';
      this.cdr.detectChanges();
    });
  }

  /**
   * Complete the animation and hide overlay
   */
  private completeAnimation(): void {
    // Shrink and fade
    this.iconTransform = 'scale(0.9)';
    this.showBackdrop = false;
    this.cdr.detectChanges();

    // Hide after animation
    setTimeout(() => {
      this.isActive = false;
      this.isAnimating = false;
      this.heroData = null;
      this.cdr.detectChanges();
    }, 300);
  }

  /**
   * Update the destination position (called by target component)
   */
  updateDestination(endRect: DOMRect): void {
    if (!this.isAnimating) return;

    this.iconPosition = {
      left: endRect.left,
      top: endRect.top,
      width: endRect.width,
      height: endRect.height
    };
    this.cdr.detectChanges();
  }
}
